/**
 * One call, over the list it came from.
 *
 * IT USED TO BE A PAGE, and the page was the problem. Opening a call navigated away from
 * `/calls`, which threw away the filters' scroll position and every page of rows loaded
 * past the first - so working through a morning's calls meant returning to the top of the
 * list twenty times. A dialog keeps the list mounted underneath, and `/calls/:id` is still
 * a real URL: this is a CHILD ROUTE of the list, not a piece of component state, so a link
 * out of an email, the browser's back button and the overview's three links all still work.
 *
 * SIXTY / FORTY, AND THE SPLIT IS THE ARGUMENT. The left is the call itself - the only
 * thing on screen that is the conversation rather than somebody's account of it - and the
 * right is the account: the summary, the grade, the gaps, the business's own fields. They
 * scroll independently because they are read at different speeds; you skim one and search
 * the other. Under 900px there is no such thing as two columns, so they stack and the
 * whole body takes one scroll - two nested scrollers on a phone is a trap, not a layout.
 *
 * THE HEADER IS THE RECORD'S IDENTITY, and it is drawn from the ROW where it can be. The
 * list already holds a `CallSummary` for the open call, so stepping to the next one changes
 * the name, the badge and the time instantly and fills the panes a beat later. Waiting for
 * the fetch to draw a name we already have reads as slow for no reason.
 *
 * NO ARROW KEYS, and that is deliberate rather than forgotten: Left and Right are how you
 * scroll a pane, and this dialog has two of them.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi, type CallDetail as Call, type CallSummary } from "../api";
import { useCallList } from "./Calls";
import { Dialog } from "../components/Dialog";
import { CallReading } from "../components/CallReading";
import { Conversation } from "../components/Conversation";
import { ToolCalls } from "../components/ToolCalls";
import { RecordingPlayer } from "../components/RecordingPlayer";
import { OutcomeBadge } from "../components/OutcomeBadge";
import { QualityInline } from "../components/Qualification";
import { Empty, Skeleton, SkeletonRows } from "../components/States";
import { Check, ChevronLeft, ChevronRight, Copy, Search } from "../components/Icons";
import { useCurrentAgent } from "../currentAgent";
import { Chip, IconButton, cx } from "../ui";
import { humanise } from "../format";
import { duration, fullWhen, handle, numberOf, phone } from "../format";
import { HandledControl } from "../components/Handled";
import { useScopedTo } from "../components/Shell";

/** A disabled `IconButton` still lights up on hover. Fixed here rather than in `ui.tsx`. */
const STEP = "disabled:pointer-events-none disabled:opacity-40";

/**
 * `/calls/:id`, in whichever view this business reads.
 *
 * THE CHILD ROUTE STAYS IN BOTH, which is what keeps a link out of an email, the back button
 * and every `hrefFor` working whichever view a business is on. In the simplified register
 * the call is not a dialog over the list but the pane beside it, and that page reads the id
 * out of the address bar itself - so here the route renders nothing, rather than a second
 * copy of the same call on top of the first.
 */
export function CallRoute() {
  const { current } = useCurrentAgent();
  return current?.view === "simple" ? null : <CallDialog />;
}

export function CallDialog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const to = useScopedTo();
  const { current } = useCurrentAgent();
  const { calls, hrefFor, listHref, loadMore, hasMore, replace } = useCallList();
  const { data, error, loading } = useApi<{ call: Call }>(id ? `/v1/calls/${id}` : null);
  const [stepping, setStepping] = useState(false);
  /*
   * THE CALL AS THE LAST WRITE RETURNED IT, laid over the fetched one. Marking a call
   * handled answers with the whole call, re-read, so there is nothing left to fetch - and
   * `reload()` would put both panes back to skeletons for a beat over a change that only
   * touched one line of the header. Keyed by id, so stepping to the next call drops it.
   */
  const [saved, setSaved] = useState<Call | null>(null);
  const detail = saved && String(saved.id) === id ? saved : (data?.call ?? null);

  const index = calls.findIndex((call) => String(call.id) === id);
  /*
   * WHERE THE CLOSE GOES. `navigate(-1)` when the reader got here from inside the app, so
   * opening a call from the overview's "waiting on you" panel and closing it puts them back
   * on the overview rather than on a list they never asked for. On a link out of an email
   * there is no in-app entry behind this one and `-1` would leave the application, so that
   * case goes to the list - carrying the filters, which is why it is `listHref` and not
   * `"/calls"`. Prev/next replace rather than push, so they never lengthen what `-1` steps
   * over.
   *
   * IT ASKS `idx`, NOT `location.key`. The key looks like the right test - React Router
   * documents `"default"` for the entry a browser landed on - but something in the shell
   * replaces the URL during boot, which mints a real key while the entry is still the
   * first one. A cold deep link then took the `-1` branch and left the app. `idx` counts
   * the entries this document actually pushed and a replace does not increment it, which
   * is the question being asked: is there anywhere of ours to go back to.
   */
  const close = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(listHref);
  };

  async function step(delta: -1 | 1) {
    if (index < 0) return;
    const next = index + delta;
    if (next >= 0 && next < calls.length) {
      navigate(hrefFor(calls[next]!.id), { replace: true });
      return;
    }
    if (delta === 1 && hasMore) {
      setStepping(true);
      // `loadMore` resolves with what it appended, and that is what makes this safe: `calls`
      // is the array from before the await and is stale by the time we read it again.
      const added = await loadMore();
      setStepping(false);
      if (added[0]) navigate(hrefFor(added[0].id), { replace: true });
    }
  }

  // The row we came from, which is enough to draw the header. `data` fills in the rest.
  const row: CallSummary | Call | undefined = detail ?? calls[index];
  const tz = current?.timezone;
  // The customer's number whichever way the call went, and the one rule for it in the
  // product. See `numberOf`: on a call the agent placed, `caller` is the shop's own line.
  const number = row ? numberOf(row) : null;
  const words = current?.vocabulary.labels ?? {};

  return (
    <Dialog
      size="wide"
      bare
      onClose={close}
      title={
        row ? (
          /* The same handle the row carries, so the dialog and the row it was opened from
             agree - which they did not always do, because the row read the summary's
             contact and this read the detail's. The contact's name is still in the reading
             column below, where it is one thing the agent recorded rather than the title
             of the record. */
          handle(row.callId)
        ) : error ? (
          /* The record's reference, which is the only thing we know about a call that did
             not load - and the same fallback the page this replaced used for a call with
             nothing in it. A skeleton here would be an animation promising a name that is
             never coming, and repeating "No such call" would say it twice. */
          `Call ${id}`
        ) : (
          <Skeleton width={220} height={20} />
        )
      }
      lead={
        /* THE TWO GRADES, BESIDE THE NAME AND NOT INSIDE IT. The agent's own label and the
           platform's meter say different things - see the note on `Row` in `Calls.tsx` -
           and the header is the one place in the product with room to print both without
           the reader having to work out which of two words on one line to believe. The
           meter is here rather than 400px down the reading column because it is the reason
           somebody opened this call, and it must not scroll away. */
        row && (
          <>
            <OutcomeBadge
              label={row.outcome.label}
              group={row.outcome.group}
              priority={row.outcome.priority}
            />
            <QualityInline qualification={row.analysis.qualification} labels={words} />
          </>
        )
      }
      meta={
        row && (
          <>
            {/* Click-to-call, because the whole reason somebody opens a hot lead is to ring
                the person back. In the header rather than in the reading column, so it is
                still there when that column has been scrolled to the bottom. */}
            {number && (
              <span className="flex items-center gap-0.5">
                <a href={`tel:${number.replace(/\s/g, "")}`}>{phone(number)}</a>
                <CopyNumber number={number} />
              </span>
            )}
            {/* WHO THIS CALL IS FILED UNDER, as a link to them. Beside the number and not in
                the title: the title is the call's own handle, and a name there is the agent's
                word or a person's filing, never the call's. Only on the detail, which is the
                one response that carries the contact's current name. */}
            {detail?.contactRef && (
              <Link
                to={to(`/contacts/${detail.contactRef.id}`)}
                className="font-medium text-ink underline decoration-line-strong underline-offset-2 hover:decoration-ink"
              >
                {detail.contactRef.name ?? "Their contact"}
              </Link>
            )}
            {/*
              ONE SENTENCE, ON ITS OWN LINE. `meta` is a wrapping flex row, so without the
              `w-full` these clauses would reflow into whatever gap the badge and the number
              left - and "handed off by sms" would end up beside a phone number, reading as
              part of it. The dots are the sentence's punctuation and are hidden from a
              screen reader, which gets the clauses as three separate phrases anyway.
            */}
            <span className="flex w-full flex-wrap items-center gap-x-1.5">
              <span>{fullWhen(row.startedAt, tz)}</span>
              <span aria-hidden="true">·</span>
              <span>{duration(row.durationMs)}</span>
              {/* WHERE THE CALL WENT, said once and said here. It used to be a chip in the
                  reading column's outcome section; up here it is legible without scrolling
                  and the section below is free to be about what to DO rather than about
                  where it was sent. `none` is not a handoff and does not print. */}
              {row.outcome.handoff && row.outcome.handoff !== "none" && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    handed off by{" "}
                    <strong className="font-semibold text-ink">
                      {humanise(row.outcome.handoff)}
                    </strong>
                  </span>
                </>
              )}
              {/* WHO DEALT WITH IT, in the same sentence as when it happened. The control
                  in the reading column says it again with the Reopen beside it; up here it
                  is what somebody stepping through a morning's calls reads without
                  scrolling. */}
              {row.handled && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    handled
                    {row.handled.by && (
                      <>
                        {" "}by <strong className="font-semibold text-ink">{row.handled.by}</strong>
                      </>
                    )}
                  </span>
                </>
              )}
              {row.mode !== "live" && <Chip>{row.mode}</Chip>}
            </span>
          </>
        )
      }
      aside={
        /* NO ARROWS AT ALL when this call is not in the list behind the dialog - a link out
           of an email, or a call the current filter excludes. Two permanently dead buttons
           say the feature is broken; their absence says there is nothing to step through. */
        index >= 0 ? (
          <>
            <IconButton
              type="button"
              className={STEP}
              disabled={index === 0}
              onClick={() => void step(-1)}
              title="Previous call"
            >
              <ChevronLeft size={17} />
              <span className="sr-only">Previous call</span>
            </IconButton>
            <span className="nums px-1 text-2xs whitespace-nowrap text-ink-faint">
              {/* The `+` is the honest way to say "of the ones loaded so far". */}
              {index + 1} of {calls.length}
              {hasMore ? "+" : ""}
            </span>
            <IconButton
              type="button"
              className={cx(STEP, "mr-1")}
              disabled={stepping || (index === calls.length - 1 && !hasMore)}
              onClick={() => void step(1)}
              title={stepping ? "Loading more calls" : "Next call"}
            >
              <ChevronRight size={17} />
              <span className="sr-only">Next call</span>
            </IconButton>
          </>
        ) : undefined
      }
    >
      {/* AN ERROR IS NOT A SPLIT. A transcript pane holding an error message beside an
          empty reading column, with a rule down the middle, draws two panes for a call
          that has neither. One column, and the message is the whole body. */}
      {error ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <Empty
            compact
            /* Following a three-month-old link out of an email is not a failure, and
               painting a red triangle at somebody for doing it says it is. The 404 keeps
               the copy - it is the best sentence this screen ever had - and loses the tone. */
            tone={error.status === 404 ? undefined : "alert"}
            icon={error.status === 404 ? Search : undefined}
            title={error.status === 404 ? "No such call" : "Couldn't load this call"}
            body={
              error.status === 404
                ? "It may belong to another organization, or it may have aged out of the record."
                : error.message
            }
            action={{ label: "Back to all calls", onClick: close }}
          />
        </div>
      ) : (
      <div
        className={cx(
          "grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)]",
          /*
           * `content-start` GOES WITH THE `min-h-auto` BELOW, and the pair is what makes
           * the stacked case work. `min-h-0` is what lets a pane scroll instead of growing
           * - and it is also what lets its grid ROW shrink below its content, so stacked,
           * the two rows were each given half the dialog while their content was three
           * times that, and with `overflow-visible` the reading painted straight over the
           * transcript. Content-sized rows, packed at the top, and the container scrolls
           * past both.
           */
          "max-[900px]:grid-cols-[minmax(0,1fr)] max-[900px]:content-start",
          "max-[900px]:overflow-y-auto",
        )}
      >
        {/* `@container` HERE TOO, AND NOT ONLY ON THE ASIDE. `Conversation` measures at
            640px, and on a 1440px dialog this pane is about 864px while the window is
            1600 - so a window query fires the narrow layout on a pane that is nowhere near
            narrow, or fails to fire on one that is. Same bug, same fix, as the note in
            `Qualification.tsx` and the one in `Fields.tsx`. */}
        <div className="@container min-h-0 overflow-y-auto px-5 pb-4 max-[900px]:min-h-auto max-[900px]:overflow-visible">
          {loading || !data ? (
            <div className="pt-4">
              <SkeletonRows rows={8} />
            </div>
          ) : (
            <>
              {/*
                THE HEADING OWNS THE PANE'S TOP PADDING, and that is the whole fix rather
                than a tidy-up. A sticky box is held inside its scroller's CONTENT box, so
                with `py-4` on the pane it stuck 18px down and the transcript scrolled up
                through the strip of white above it - a turn cut in half, hanging over the
                rule. The pane keeps `pb-4`; the top padding moved onto this element, so
                what is stuck starts at the very top of the scrollport and there is no band
                left for anything to show through.

                The text still lands where it did, which is what keeps this level with the
                reading column's first line across the rule. `-mx-5 px-5` so the rule
                reaches the pane's edges rather than stopping at the text.
              */}
              <div className="sticky top-0 z-10 -mx-5 mb-2 grid gap-2 border-b border-line-soft bg-paper px-5 pt-4 pb-2">
                <h3 className="text-2xs font-semibold tracking-[0.06em] text-ink-soft uppercase">
                  The conversation
                </h3>
                {/*
                  THE RECORDING RIDES THE HEADING, INSIDE WHAT IS STUCK. It belongs to the
                  whole pane rather than to the top of the transcript: somebody who has read
                  four hundred words down and wants to hear how a sentence was said should not
                  have to scroll back for the play button. It is also why this heading became
                  a `<div>` with an `<h3>` inside it - a sticky box is held inside its
                  scroller's content box, so the transport had to join what is stuck rather
                  than sit above it and scroll away.

                  ABSENT, NOT DISABLED, when there is no recording. That is the state of every
                  call taken before this existed and of every business with recording switched
                  off, and a dead transport on all of them would read as a broken feature
                  rather than as a call nobody recorded.
                */}
                {data.call.recording && (
                  <RecordingPlayer
                    path={`/v1/calls/${data.call.id}/recording?v=${data.call.recording.sha256}`}
                    durationMs={data.call.recording.durationMs}
                    className="min-w-0"
                  />
                )}
              </div>
              <Conversation bare turns={data.call.transcript} events={data.call.timeline} />
            </>
          )}
        </div>

        {/* `@container` is what `Fields` and `QualificationDetail` measure against. Without
            it both read the WINDOW, and a 440px column on a 1440px screen gets the wide
            layout - which is the bug their own comments are about. */}
        <aside
          className={cx(
            "@container min-h-0 overflow-y-auto border-l border-line px-5 py-4",
            "max-[900px]:min-h-auto max-[900px]:overflow-visible",
            "max-[900px]:border-t max-[900px]:border-l-0",
          )}
        >
          {/*
            THE ONE THING IN THIS COLUMN SOMEBODY DOES, AND IT IS ABOVE THE READING RATHER
            THAN INSIDE IT. `CallReading` swaps itself for a single sentence on a call with
            no substance - a hang-up, a silent line - and those are exactly the calls a person
            most often has to chase. Drawn from the ROW, like the header, so it is there
            before the detail has loaded.
          */}
          {row && (
            <section className="mb-3.5 border-b border-line-soft pb-3.5">
              <h3 className="mb-2 text-2xs font-semibold tracking-[0.06em] text-ink-soft uppercase">
                {row.handled ? "Handled" : row.needsPerson ? "Waiting on you" : "Follow-up"}
              </h3>
              <HandledControl
                call={row}
                onChanged={(next) => {
                  // The row behind the dialog, and this dialog's own header, in one step.
                  replace(next);
                  setSaved(next);
                }}
              />
            </section>
          )}
          {loading || !data ? (
            <div className="grid gap-3.5">
              <Skeleton height={90} />
              <SkeletonRows rows={4} />
            </div>
          ) : (
            <>
              <CallReading call={data.call} />
              <ToolCalls calls={data.call.toolCalls ?? []} />
            </>
          )}
        </aside>
      </div>
      )}
    </Dialog>
  );
}

/** The number, on the clipboard. Same copied/tick beat as `CopyButton`, at icon size. */
export function CopyNumber({ number }: { number: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      type="button"
      title="Copy the number"
      onClick={() => {
        void navigator.clipboard?.writeText(number);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      <span className="sr-only">Copy the number</span>
    </IconButton>
  );
}
